import { c as _c } from "react/compiler-runtime";
// @ts-nocheck - framer-motion LazyMotion type compatibility
import { LazyMotion, domAnimation, m } from "framer-motion";
import React, { forwardRef, memo, useId } from "react";
const ICON_VARIANTS = {
  left: {
    initial: {
      scale: 0.8,
      opacity: 0,
      x: 0,
      y: 0,
      rotate: 0
    },
    animate: {
      scale: 1,
      opacity: 1,
      x: 0,
      y: 0,
      rotate: -6,
      transition: {
        duration: 0.4,
        delay: 0.1
      }
    },
    hover: {
      x: -22,
      y: -5,
      rotate: -15,
      scale: 1.1,
      transition: {
        duration: 0.2
      }
    }
  },
  center: {
    initial: {
      scale: 0.8,
      opacity: 0
    },
    animate: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.4,
        delay: 0.2
      }
    },
    hover: {
      y: -10,
      scale: 1.15,
      transition: {
        duration: 0.2
      }
    }
  },
  right: {
    initial: {
      scale: 0.8,
      opacity: 0,
      x: 0,
      y: 0,
      rotate: 0
    },
    animate: {
      scale: 1,
      opacity: 1,
      x: 0,
      y: 0,
      rotate: 6,
      transition: {
        duration: 0.4,
        delay: 0.3
      }
    },
    hover: {
      x: 22,
      y: -5,
      rotate: 15,
      scale: 1.1,
      transition: {
        duration: 0.2
      }
    }
  }
};
const CONTENT_VARIANTS = {
  initial: {
    y: 20,
    opacity: 0
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      delay: 0.2
    }
  }
};
const BUTTON_VARIANTS = {
  initial: {
    y: 20,
    opacity: 0
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      delay: 0.3
    }
  }
};
export const cn = (...classes: (string | boolean | undefined)[]) => classes.filter(Boolean).join(" ");
interface IconContainerProps {
  children: React.ReactNode;
  variant: "left" | "center" | "right";
  className?: string;
}
const IconContainer = memo(t0 => {
  const $ = _c(8);
  const {
    children,
    variant,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = ICON_VARIANTS[variant];
  let t3;
  if ($[0] !== className) {
    t3 = cn("shadow-ds-elev-2 relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300", "border-ds-border-default bg-ds-surface-card group-hover:border-ds-neutral-300 dark:group-hover:border-ds-neutral-700 border group-hover:shadow-xl", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children) {
    t4 = <div className="text-ds-text-muted group-hover:text-ds-text-secondary text-sm transition-colors duration-300">{children}</div>;
    $[2] = children;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = <m.div variants={t2} className={t3}>{t4}</m.div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
});
IconContainer.displayName = "IconContainer";
interface MultiIconDisplayProps {
  icons: React.ReactNode[];
}
const MultiIconDisplay = memo(t0 => {
  const $ = _c(10);
  const {
    icons
  } = t0;
  if (!icons || icons.length < 3) {
    return null;
  }
  let t1;
  if ($[0] !== icons[0]) {
    t1 = <IconContainer variant="left" className="left-2 top-1 z-10">{icons[0]}</IconContainer>;
    $[0] = icons[0];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icons[1]) {
    t2 = <IconContainer variant="center" className="z-20">{icons[1]}</IconContainer>;
    $[2] = icons[1];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== icons[2]) {
    t3 = <IconContainer variant="right" className="right-2 top-1 z-10">{icons[2]}</IconContainer>;
    $[4] = icons[2];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="relative isolate flex justify-center">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
});
MultiIconDisplay.displayName = "MultiIconDisplay";
const Background = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden="true" className="absolute inset-0 dark:opacity-0" style={{
      background: `radial-gradient(ellipse 500px 350px at center, rgba(249, 250, 251, 0.4) 0%, rgba(243, 244, 246, 0.2) 30%, rgba(243, 244, 246, 0.1) 50%, transparent 70%)`
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden="true" className="absolute inset-0 opacity-0 dark:opacity-100" style={{
      background: `radial-gradient(ellipse 500px 350px at center, rgba(39, 39, 42, 0.15) 0%, rgba(24, 24, 27, 0.1) 30%, rgba(18, 18, 18, 0.05) 50%, transparent 70%)`
    }} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <>{t0}{t1}<div aria-hidden="true" className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-[0.02] dark:group-hover:opacity-[0.01]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 1px)`,
        backgroundSize: "24px 24px"
      }} /></>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
interface EmptyStateProps {
  title: string;
  description?: string;
  icons?: React.ReactNode[];
  action?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
  };
  variant?: "default" | "subtle" | "error";
  size?: "sm" | "default" | "lg";
  isIconAnimated?: boolean;
  className?: string;
}
export const EmptyState = forwardRef<HTMLElement, EmptyStateProps>((t0, ref) => {
  const $ = _c(55);
  const {
    title,
    description,
    icons,
    action,
    variant: t1,
    size: t2,
    isIconAnimated: t3,
    className: t4
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "default" : t2;
  const isIconAnimated = t3 === undefined ? true : t3;
  const className = t4 === undefined ? "" : t4;
  const titleId = useId();
  const descriptionId = useId();
  let T0;
  let T1;
  let getButtonClasses;
  let getTextClasses;
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== descriptionId || $[2] !== ref || $[3] !== size || $[4] !== titleId || $[5] !== variant) {
    const sizeClasses = {
      sm: "p-2",
      default: "p-4",
      lg: "p-6"
    };
    const getVariantClasses = _temp;
    getTextClasses = _temp2;
    getButtonClasses = _temp3;
    T1 = LazyMotion;
    t10 = domAnimation;
    T0 = m.section;
    t5 = ref;
    t6 = "region";
    t7 = titleId;
    t8 = descriptionId;
    t9 = cn("group transition-all duration-300 rounded-xl relative overflow-hidden text-center flex flex-col items-center justify-center", sizeClasses[size], getVariantClasses(variant), className);
    $[0] = className;
    $[1] = descriptionId;
    $[2] = ref;
    $[3] = size;
    $[4] = titleId;
    $[5] = variant;
    $[6] = T0;
    $[7] = T1;
    $[8] = getButtonClasses;
    $[9] = getTextClasses;
    $[10] = t10;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    getButtonClasses = $[8];
    getTextClasses = $[9];
    t10 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  const t11 = isIconAnimated ? "hover" : "animate";
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Background />;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== icons) {
    t13 = icons && <div className="mb-6"><MultiIconDisplay icons={icons} /></div>;
    $[17] = icons;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== getTextClasses || $[20] !== size) {
    t14 = getTextClasses("title", size);
    $[19] = getTextClasses;
    $[20] = size;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== title || $[24] !== titleId) {
    t15 = <h2 id={titleId} className={t14}>{title}</h2>;
    $[22] = t14;
    $[23] = title;
    $[24] = titleId;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== description || $[27] !== descriptionId || $[28] !== getTextClasses || $[29] !== size) {
    t16 = description && <p id={descriptionId} className={cn(getTextClasses("description", size).replace("font-semibold", ""), "max-w-md leading-relaxed")}>{description}</p>;
    $[26] = description;
    $[27] = descriptionId;
    $[28] = getTextClasses;
    $[29] = size;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t15 || $[32] !== t16) {
    t17 = <m.div variants={CONTENT_VARIANTS} className="mb-6 space-y-2">{t15}{t16}</m.div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== action || $[35] !== getButtonClasses || $[36] !== size) {
    t18 = action && <m.div variants={BUTTON_VARIANTS}><m.button type="button" onClick={action.onClick} disabled={action.disabled} className={getButtonClasses(size)} whileTap={{
        scale: 0.98
      }}>{action.icon && <m.div className="transition-transform group-hover/button:rotate-90" whileHover={{
          rotate: 90
        }}>{action.icon}</m.div>}<span className="relative z-10">{action.label}</span></m.button></m.div>;
    $[34] = action;
    $[35] = getButtonClasses;
    $[36] = size;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t13 || $[39] !== t17 || $[40] !== t18) {
    t19 = <div className="relative z-10 flex flex-col items-center">{t13}{t17}{t18}</div>;
    $[38] = t13;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== T0 || $[43] !== t11 || $[44] !== t19 || $[45] !== t5 || $[46] !== t6 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    t20 = <T0 ref={t5} role={t6} aria-labelledby={t7} aria-describedby={t8} className={t9} initial="initial" animate="animate" whileHover={t11}>{t12}{t19}</T0>;
    $[42] = T0;
    $[43] = t11;
    $[44] = t19;
    $[45] = t5;
    $[46] = t6;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== T1 || $[52] !== t10 || $[53] !== t20) {
    t21 = <T1 features={t10}>{t20}</T1>;
    $[51] = T1;
    $[52] = t10;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
});
EmptyState.displayName = "EmptyState";
function _temp(variant_0) {
  const variants = {
    default: "bg-white/50 border-dashed border-2 border-gray-300 hover:border-gray-400 dark:bg-transparent dark:border-transparent dark:hover:border-transparent",
    subtle: "bg-white/30 border border-transparent hover:bg-gray-50/30 dark:bg-transparent dark:hover:bg-transparent",
    error: "bg-ds-surface-card border border-red-200 bg-red-50/50 hover:bg-red-50/80 dark:border-red-800 dark:bg-red-950/50 dark:hover:bg-red-950/80"
  };
  return variants[variant_0];
}
function _temp2(type, size_0) {
  const sizes = {
    title: {
      sm: "text-base",
      default: "text-lg",
      lg: "text-xl"
    },
    description: {
      sm: "text-xs",
      default: "text-sm",
      lg: "text-base"
    }
  };
  const colors = {
    title: "text-gray-900 dark:text-zinc-100",
    description: "text-gray-600 dark:text-ds-text-disabled"
  };
  return cn(sizes[type][size_0], colors[type], "font-semibold transition-colors duration-200");
}
function _temp3(size_1) {
  const sizeClasses_0 = {
    sm: "text-xs px-3 py-1.5",
    default: "text-sm px-4 py-2",
    lg: "text-base px-6 py-3"
  };
  return cn("inline-flex items-center gap-2 border rounded-md font-medium shadow-ds-soft hover:shadow-md transition-all duration-200 relative overflow-hidden group/button disabled:opacity-50 disabled:cursor-not-allowed", "border-gray-300 bg-white hover:bg-ds-surface-hover text-gray-700", "dark:border-zinc-600 dark:bg-ds-neutral-800 dark:hover:bg-ds-neutral-700 dark:text-zinc-200", sizeClasses_0[size_1]);
}